import type { ClientIncentive, ClientLabel } from './client'

export type ListingFile = {
  id?: string
  title?: string
  url: string
}

export type ListingImage = {
  id?: string
  thumbnail_url: string
  title?: string
  url: string
}

export type ListingLocation = {
  coordinates: number[]
  type: string
}

export type ListingMeta = {
  acres?: number
  amps?: string
  building_class?: string
  building_sf?: number
  cap_rate?: number
  ceiling_height?: string
  clear_height?: string
  column_spacing?: string
  construction_status?: string
  construction_type: string
  description?: string
  dock_number?: number
  drive_in_number?: number
  floors?: number
  highlights?: string
  max_available_sf?: number
  max_contiguous?: number
  max_lease_rate?: number
  max_sf?: number
  min_available_sf?: number
  min_contiguous?: number
  min_lease_rate?: number
  min_sf?: number
  occupancy?: number
  office_sf?: number
  parking_count?: number
  parking_spaces?: number
  power_providers: string[]
  power_type?: string
  price?: number
  property_subtypes?: string[]
  rail?: boolean
  rail_near?: boolean
  sale_type?: string
  sprinklers?: boolean
  structure?: string
  three_phase?: boolean
  total_units?: number
  vehicles_per_day?: number
  year_built?: number
  year_renovated?: number
  zoning?: string
  zoning_description?: string
  opportunity_zone?: string
  virtual_tour_link?: string
}

export type ListingOrganization = {
  id: string
  name: string
  website?: string
  logo?: string
}

export type ListingProfile = {
  company?: string
  email?: string
  first_name: string
  id?: string
  last_name: string
  mobile?: string
  office?: string
  title?: string
}

export type ListingProperty = {
  address?: string
  address_2?: string
  city?: string
  county?: string
  id?: string
  state_abbr: string
  zip_code?: string
  latitude: number
  longitude: number
}

export type ListingSuite = {
  id: string
  acres?: number
  available: boolean
  available_date?: string
  description?: string
  max_available_sf?: number
  min_available_sf?: number
  rate_max?: number
  rate_min?: number
  rate_type?: string
  sublease: boolean
  unit?: string
}
export type ListingSpace = ListingSuite

export type Listing = {
  _id: string
  id: string
  tid: string
  slug: string
  featured?: boolean
  labels?: ClientLabel[]
  incentives?: ClientIncentive[]
  approved: boolean
  created: Date
  files: ListingFile[]
  images: ListingImage[]
  location: ListingLocation
  meta: ListingMeta
  modified: Date
  name?: string
  organization: ListingOrganization
  profiles: ListingProfile[]
  property: ListingProperty
  property_id?: number
  property_listing_id?: number
  property_types: string[]
  source: string
  source_url?: string
  status: string
  suites: ListingSuite[]
  type: string
}

type MetaFormats = 'currency' | 'number'

export type MetaFieldConfig = {
  label?: string
  format?:
    | MetaFormats
    | ((
        v: any,
        meta: {
          [key: string]: any
        },
        valuesOnly: boolean
      ) => string | null | undefined)
  prefix?: string
  suffix?: string
  render?: boolean
  generated?: boolean
}

export const META_FIELDS: { [key: string]: MetaFieldConfig } = {
  property_subtypes: { render: false }, // Rendered after property types
  description: { render: false },
  highlights: { render: false },

  price: {
    label: 'Price',
    format: 'currency',
  },
  price_per_acre: {
    label: 'Price per Acre',
    prefix: '$',
    suffix: '/acre',
  },
  price_per_sf: {
    label: 'Price per Sf',
    prefix: '$',
    suffix: '/SF',
  },
  price_per_unit: {
    label: 'Price per Unit',
    prefix: '$',
    suffix: '/unit',
  },
  cap_rate: {
    label: 'Cap Rate',
    suffix: '%',
  },
  building_sf: {
    label: 'Building SF',
    suffix: 'SF',
  },
  acres: {
    label: 'Acres',
    format: (v: string) => {
      return v.toLocaleString()
    },
  },
  min_contiguous: {
    label: 'Min Contiguous',
    suffix: 'SF',
  },
  max_contiguous: {
    label: 'Max Contiguous',
    suffix: 'SF',
  },
  zoning: {
    label: 'Zoning',
  },
  zoning_description: {
    label: 'Zoning Description',
  },
  submarket: {
    label: 'Submarket',
  },
  opportunity_zone: {
    label: 'In Opportunity Zone',
  },
  building_class: {
    label: 'Building Class',
  },
  three_phase: {
    label: 'Three phase',
  },
  sprinklers: {
    label: 'Sprinkler',
  },
  ceiling_height: {
    label: 'Ceiling Height',
    suffix: 'ft',
  },
  clear_height: {
    label: 'Clear Height',
    suffix: 'ft',
  },
  column_spacing: {
    label: 'Column Spacing',
  },
  rail: {
    label: 'Rail Served',
  },
  floors: {
    label: 'Floors',
  },
  year_built: {
    label: 'Year Built',
  },
  year_renovated: {
    label: 'Year Renovated',
  },
  parking_spaces: {
    label: 'Parking Spaces',
  },
  vehicles_per_day: {
    label: 'Vehicles per Day',
    suffix: 'VPD',
  },

  adjacent_parcels_available: {
    label: 'Adjacent Parcels Available',
  },
  average_occupancy: {
    label: 'Average Occupancy',
  },
  building_count: {
    label: 'Building Count',
  },
  cam_charge: {
    label: 'Cam Charge',
  },
  construction_status: {
    label: 'Construction Status',
  },
  construction_type: {
    label: 'Construction Type',
    format: (v) => {
      if (v.length === 0) {
        return null
      } else if (v.length === 1) {
        return v
      } else return v.join(', ')
    },
  },
  crane_number: {
    label: 'Crane Number',
  },
  crane_underhook_height: {
    label: 'Crane Underhook Height',
  },
  crane_weight: {
    label: 'Crane Weight',
  },
  crane: {
    label: 'Crane',
  },
  dock_number: {
    label: 'Dock Number',
  },
  dock_size: {
    label: 'Dock Size',
  },
  drive_in_number: {
    label: 'Drive in Number',
  },
  drive_in_size: {
    label: 'Drive in Size',
  },
  eave_height: {
    label: 'Eave Height',
    suffix: 'ft',
  },
  flood_plain: {
    label: 'Flood Plain',
  },
  floor_bearing_capacity: {
    label: 'Floor Bearing Capacity',
  },
  floor_thickness: {
    label: 'Floor Thickness',
  },
  frontage: {
    label: 'Frontage',
    suffix: 'ft',
  },
  hvac: {
    label: 'HVAC',
  },
  land_splits_available: {
    label: 'Land Splits Available',
  },
  laundry: {
    label: 'Laundry',
  },
  lift_roof_possible: {
    label: 'Lift Roof Possible',
  },

  // min_divisible: {
  //   label: 'Min Divisible',
  //   suffix: 'SF',
  // },
  nearest_airport: {
    label: 'Nearest Airport',
  },
  nearest_highway: {
    label: 'Nearest Highway',
  },
  nearest_interstate: {
    label: 'Nearest Interstate',
  },
  nearest_port: {
    label: 'Nearest Port',
  },
  office_sf: {
    label: 'Office Sf',
    suffix: 'SF',
  },
  occupancy: {
    label: 'Occupancy',
  },
  parking_ratio: {
    label: 'Parking Ratio',
    format: (v: string) => {
      return v.includes('1000') ? v : `${v}/1000`
    },
  },
  refrigerated: {
    label: 'Refrigerated',
  },
  reinforced_floor: {
    label: 'Reinforced Floor',
  },
  sale_type: {
    label: 'Sale Type',
  },
  soil_type: {
    label: 'Soil Type',
  },
  sprinkler_type: {
    label: 'Sprinkler Type',
  },
  structure: {
    label: 'Structure',
    render: false,
  },
  tax_parcel_id: {
    label: 'Tax Parcel ID',
  },
  topography: {
    label: 'Topography',
  },
  total_bed_count: {
    label: 'Total Bed Count',
  },
  total_units: {
    label: 'Total Units',
  },
  trailer_parking_spaces: {
    label: 'Trailer Parking Spaces',
  },
  virtual_tour_link: {
    label: 'Virtual Tour Link',
    render: false,
  },
  yard: {
    label: 'Yard',
  },
  distance_to_airport: {
    label: 'Distance to Airport',
    suffix: 'mi',
  },
  distance_to_highway: {
    label: 'Distance to Highway',
    suffix: 'mi',
  },
  distance_to_interstate: {
    label: 'Distance to Interstate',
    suffix: 'mi',
  },
  distance_to_port: {
    label: 'Distance to Port',
    suffix: 'mi',
  },
  power_amps: {
    label: 'Power Amps',
    suffix: 'A',
  },
  natural_gas_provider: {
    label: 'Natural Gas Provider ',
  },
  natural_gas: {
    label: 'Natural Gas',
  },
  // power_provider: {
  //   label: 'Power Provider',
  // },
  power_providers: {
    label: 'Power Providers',
    format: (v) => {
      if (v.length === 0) {
        return null
      } else if (v.length === 1) {
        return v
      } else return v.join(', ')
    },
  },
  power_type: {
    label: 'Power Type',
  },
  power_voltage: {
    label: 'Power Voltage',
    suffix: 'V',
  },
  power: {
    label: 'Power',
  },
  rail_near: {
    label: 'Rail Near',
  },
  rail_provider: {
    label: 'Rail Provider',
  },
  rail_type: {
    label: 'Rail Type',
  },
  sewer_capacity: {
    label: 'Sewer Capacity',
  },
  sewer_line_size: {
    label: 'Sewer Line Size',
  },
  sewer_provider: {
    label: 'Sewer Provider',
  },
  sewer: {
    label: 'Sewer',
  },
  water_capacity: {
    label: 'Water Capacity',
  },
  water_line_size: {
    label: 'Water Line Size',
  },
  water_provider: {
    label: 'Water Provider',
  },
  water: {
    label: 'Water',
  },
}
